// rulesetSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Node Name Constants
const CATEGORY_CHECK = "Category Check";
const ALL_OF = "All Rules to Apply";
const ONE_OF = "Min One Rule to Apply";
const LAUNCH_PROTOCOL = "Launch Protocol";
const CHECK_RANGE = "Check Range";
const CHECK_PREVIOUS_RANGE = "Check Previous Range";
const KEYWORD_CHECK = "Keyword Check";
const CHECK_MEDICATION_ISSUED = "Check Medication Issued";
const CALCULATE_QRISK = "Calculate QRisk";
const QRISK_RANGE_CHECK = "QRisk Range Check";
const LEAVE_FOR_GP = "Leave for GP";
const FILE_REPORT = "File Report";
const SEND_ACCURX_MESSAGE = "Send Accurx Message";
const CHANGE_OWNER = "Change Owner";
const ADD_CODE = "Add Code";
const CREATE_TASK = "Create Task";

// Outcome Name Constants
const ALL_PASSED = "All Passed";
const FAILED = "Failed";
const PASSED = "Passed";
const NORMAL_REPORT = "Normal Report";
const ATLEAST_ONE_PASSED = "Atleast one passed";
const ALL_FAILED = "All Failed";
const IN_RANGE = "In Range";
const OUT_OF_RANGE = "Out of Range";
const IN_PER_RANGE = "In Range";
const OUT_OF_PER_RANGE = "Out of Range";
//const SUCCESS = "Success";
const MEDICATION_PRESENT = "Medications Present";
const MEDICATION_NOT_PRESENT = "Medications Not Present";
const INSIDE_QRISK_SCORE = "Inside QRisk Score";
const OUTSIDE_QRISK_SCORE = "Outside QRisk Score";

// Save ruleset thunk
export const saveRuleset = createAsyncThunk(
  "ruleset/saveRuleset",
  async ({ ruleset, license_key, category }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/rulesets/save/`,
        {
          license_key,
          category,
          ruleset,
        },
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// Load ruleset thunk
export const loadRuleset = createAsyncThunk(
  "ruleset/loadRuleset",
  async ({ license_key, category }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/rulesets/load/`,
        {
          license_key,
          category,
        },
      );
      return res.data.ruleset.current_ruleset;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const validators = {
  [LAUNCH_PROTOCOL]: (fields) => {
    const errors = {};
    if (!fields.protocol_name || fields.protocol_name.trim() === "") {
      errors.protocol_name = "Protocol Name is required";
    }
    return errors;
  },

  [CHECK_RANGE]: (fields) => {
    const errors = {};

    if (!fields.subtest || fields.subtest.trim() === "") {
      errors.subtest = "Subtest is required";
    }

    const min =
      fields.min !== undefined && fields.min !== ""
        ? parseFloat(fields.min)
        : null;
    const max =
      fields.max !== undefined && fields.max !== ""
        ? parseFloat(fields.max)
        : null;

    if (min === null || max === null) {
      errors.min = "Min and Max value must be specified";
      errors.max = "Min and Max value must be specified";
    }

    if (min !== null && max !== null) {
      if (!(min === 0 && max === 0) && min >= max) {
        errors.max = "Max must be greater than Min";
      }
    }

    const minAge =
      fields.min_age !== undefined && fields.min_age !== ""
        ? parseInt(fields.min_age)
        : null;
    const maxAge =
      fields.max_age !== undefined && fields.max_age !== ""
        ? parseInt(fields.max_age)
        : null;

    if (minAge !== null && String(minAge) !== fields.min_age) {
      errors.min_age = "Min Age must be a positive whole number";
    }
    if (maxAge !== null && String(maxAge) !== fields.max_age) {
      errors.max_age = "Max Age must be a positive whole number";
    }

    if (minAge !== null) {
      if (!Number.isInteger(minAge) || minAge < 0) {
        errors.min_age = "Min Age must be a positive whole number";
      }
    }

    if (maxAge !== null) {
      if (!Number.isInteger(maxAge) || maxAge < 0) {
        errors.max_age = "Max Age must be a positive whole number";
      }
    }

    if (
      (minAge !== null && maxAge === null) ||
      (minAge === null && maxAge !== null)
    ) {
      errors.min_age = "Both Min Age and Max Age must be specified";
      errors.max_age = "Both Min Age and Max Age must be specified";
    }

    if (minAge !== null && maxAge !== null) {
      if (minAge > maxAge) {
        errors.max_age = "Max Age must be greater than Min Age";
      }
    }

    return errors;
  },

  [CHECK_PREVIOUS_RANGE]: (fields) => {
    const errors = {};

    if (!fields.subtest || fields.subtest.trim() === "") {
      errors.subtest = "Subtest is required";
    }

    const dropPerc =
      fields.drop_perc !== undefined && fields.drop_perc !== ""
        ? parseFloat(fields.drop_perc)
        : null;
    const gainPerc =
      fields.gain_perc !== undefined && fields.gain_perc !== ""
        ? parseFloat(fields.gain_perc)
        : null;

    if (dropPerc === null && gainPerc === null) {
      errors.drop_perc = "At least one of Drop % or Gain % must be specified";
      errors.gain_perc = "At least one of Drop % or Gain % must be specified";
    }

    if (dropPerc !== null && dropPerc < 0) {
      errors.drop_perc = "Drop % cannot be negative";
    }

    if (gainPerc !== null && gainPerc < 0) {
      errors.gain_perc = "Gain % cannot be negative";
    }

    if (!fields.previous_x_days_or_report) {
      errors.previous_x_days_or_report =
        "Search type must be specified (X Previous Days or X Previous Reports)";
    }

    if (!fields.previous_x_value || fields.previous_x_value === "") {
      errors.previous_x_value = "X Value must be specified";
    } else {
      const xValue = parseInt(fields.previous_x_value);
      if (String(xValue) !== fields.previous_x_value) {
        errors.previous_x_value = "X Value must be a positive whole number";
      }
      if (!Number.isInteger(xValue) || xValue <= 0) {
        errors.previous_x_value = "X Value must be a positive integer";
      }
    }

    return errors;
  },

  [CHECK_MEDICATION_ISSUED]: (fields) => {
    const errors = {};

    if (!fields.issued_in_months || fields.issued_in_months === "") {
      errors.issued_in_months =
        "Medication Issued in Last (Months) is required";
    } else {
      const months = parseInt(fields.issued_in_months);
      if (String(months) !== fields.issued_in_months) {
        errors.issued_in_months = "Must be a positive whole number";
      }
      if (!Number.isInteger(months) || months <= 0) {
        errors.issued_in_months = "Must be a positive integer";
      }
    }

    if (!fields.medications || fields.medications.length === 0) {
      errors.medications = "At least one medication must be specified";
    }
    return errors;
  },

  [CALCULATE_QRISK]: (fields) => {
    const errors = {};
    if (!fields.protocol_name || fields.protocol_name.trim() === "") {
      errors.protocol_name = "Protocol Name is required";
    }
    return errors;
  },

  [FILE_REPORT]: (fields) => {
    const errors = {};

    if (!fields.filing_approach) {
      errors.filing_approach =
        "Filing Approach must be specified (With Comment or No Comments)";
    }

    if (fields.filing_approach === "With Comment") {
      if (!fields.filing_comment || fields.filing_comment.trim() === "") {
        errors.filing_comment =
          "Filing Comment is required when 'With Comment' is selected";
      }
    }
    return errors;
  },

  [CHANGE_OWNER]: (fields) => {
    const errors = {};
    if (!fields.new_owner || fields.new_owner.trim() === "") {
      errors.new_owner = "Change Owner To name is required";
    }
    return errors;
  },

  [ADD_CODE]: (fields) => {
    const errors = {};
    if (!fields.code || fields.code.trim() === "") {
      errors.code = "Code is required";
    }
    return errors;
  },

  [CREATE_TASK]: (fields) => {
    const errors = {};
    if (!fields.task_type || fields.task_type.trim() === "") {
      errors.task_type = "Task Type is required";
    }
    if (!fields.due_date || fields.due_date.trim() === "") {
      errors.due_date = "Due Date is required";
    }
    if (!fields.notes || fields.notes.trim() === "") {
      errors.notes = "Notes is required";
    }
    if (!fields.clinician_name || fields.clinician_name.trim() === "") {
      errors.clinician_name = "Assign to is required";
    }
    return errors;
  },

  [LEAVE_FOR_GP]: (fields) => {
    const errors = {};
    if (!fields.info_var || fields.info_var.trim() === "") {
      errors.info_var = "Info is required";
    }
    return errors;
  },
  [QRISK_RANGE_CHECK]: (fields) => {
    const errors = {};

    const minVal =
      fields.min_val !== undefined && fields.min_val !== ""
        ? parseFloat(fields.min_val)
        : null;
    const maxVal =
      fields.max_val !== undefined && fields.max_val !== ""
        ? parseFloat(fields.max_val)
        : null;

    if (minVal === null) {
      errors.min_val = "QRisk Minimum value must be specified";
    }
    if (maxVal === null) {
      errors.max_val = "QRisk Maximum value must be specified";
    }

    if (minVal !== null && maxVal !== null) {
      if (minVal > maxVal) {
        errors.max_val = "QRisk Maximum must be greater than Minimum";
      }
    }
    return errors;
  },
};

const rulesetSlice = createSlice({
  name: "ruleset",
  initialState: {
    root: {
      id: "root",
      node: CATEGORY_CHECK,
      nodes: [],
      outcomes: {},
    },
    errors: {},
    nodeConfigs: {
      [CATEGORY_CHECK]: {
        template: (values) => {
          let parts = ["."];
          if (values.skip_without_ranges)
            parts.push(" Will be skipped if no lab ranges are present.");
          if (values.file_normal) parts.push(" Will file for normal report.");
          return parts.join(" ");
        },
        has_needsreview: true,
        has_inreview: true,
        fields_config: [
          {
            label: "Inreview:",
            var: "inreview",
            widget: "TextField",
            type: "string",
          },
          {
            label: "Needs Review and Approval:",
            var: "needs_review",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Skip if no lab ranges are present:",
            var: "skip_without_ranges",
            widget: "CheckBox",
            type: "boolean",
            messageon:
              'When selecting - Please note by selecting the "Skip if no lab ranges are present" option, ' +
              "MyBotGP will not file the test if no subtest with ranges are found.",
            messageoff:
              'When de-selecting - Please note by de-selecting the "Skip if no lab ranges are present" option, ' +
              "MyBotGP will file the test if category check pass and no subtest ranges are found.",
          },
          {
            label: "File for normal report:",
            var: "file_normal",
            widget: "CheckBox",
            type: "boolean",
            messageon:
              'When selecting - Please note by selecting the "File for Normal" option, ' +
              "MyBotGP will file based on the Lab classifying the test as Normal - all rules set " +
              "within MyBotGP will be bypassed and not applied.",
            messageoff:
              'When de-selecting - Please note by de-selecting the "File for Normal" option, ' +
              "MyBotGP will file based on the rules set within MyBotGP and not where the Lab has " +
              "classified the test as Normal.",
          },
          {
            label: "All Subtests needs to evaluate a Range Check:",
            var: "all_subtests_evaluated",
            widget: "CheckBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ALL_PASSED, FAILED, NORMAL_REPORT],
        children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
        ],
        outcome_children: [
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [ALL_OF]: {
        template: () => "All conditions must pass",
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ALL_PASSED, FAILED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [ONE_OF]: {
        template: () => "At least one condition must pass",
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [ATLEAST_ONE_PASSED, ALL_FAILED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CREATE_TASK]: {
        template: () => "Create task",
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Task type:",
            var: "task_type",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          {
            label: "Urgent:",
            var: "urgent",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Due date:",
            var: "due_date",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          {
            label: "Notes:",
            var: "notes",
            widget: "TextField",
            type: "string",
            width: 200,
          },
          // {
          //   label: "Task Category:",
          //   var: "task_category",
          //   widget: "TextField",
          //   type: "string",
          //   width: 200,
          // },
          {
            label: "Assign to:",
            var: "clinician_name",
            widget: "TextField",
            type: "string",
            width: 200,
          },
        ],
        validate: validators[CREATE_TASK],
        child_nodes: false,
      },

      [LAUNCH_PROTOCOL]: {
        template: (values) =>
          values.protocol_name
            ? `Launch Protocol: ${values.protocol_name}`
            : LAUNCH_PROTOCOL,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Protocol Name:",
            var: "protocol_name",
            widget: "TextField",
            type: "string",
            width: 200,
          },
        ],
        validate: validators[LAUNCH_PROTOCOL],
        child_nodes: false,
        outcomes: [ALL_PASSED],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CHECK_RANGE]: {
        template: (values) => {
          const parts = [];
          if (values.subtest) parts.push(`Check subtest "${values.subtest}"`);
          if (values.min != null && values.max != null)
            parts.push(`in range ${values.min} – ${values.max}`);
          else if (values.min != null) parts.push(`with minimum ${values.min}`);
          else if (values.max != null) parts.push(`with maximum ${values.max}`);
          if (values.gender) parts.push(`for ${values.gender}`);
          if (values.min_age != null || values.max_age != null)
            parts.push(
              `in age range ${values.min_age ?? "?"} – ${values.max_age ?? "?"}`,
            );
          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Subtest:",
            var: "subtest",
            widget: "TextField",
            type: "string",
          },
          {
            label: "Lab Min Value:",
            var: "lab_min",
            widget: "TextField",
            type: "float",
            width: 80,
            editable: false,
          },
          {
            label: "Lab Max Value:",
            var: "lab_max",
            widget: "TextField",
            type: "float",
            width: 80,
            editable: false,
          },
          {
            label: "Min Value:",
            var: "min",
            widget: "TextField",
            type: "float",
            width: 80,
          },
          {
            label: "Max Value:",
            var: "max",
            widget: "TextField",
            type: "float",
            width: 80,
          },
          {
            label: "Min Age:",
            var: "min_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
          {
            label: "Max Age:",
            var: "max_age",
            widget: "TextField",
            type: "int",
            width: 80,
          },
          {
            label: "Gender:",
            var: "gender",
            widget: "Combobox",
            values: ["Male", "Female"],
          },
        ],
        validate: validators[CHECK_RANGE],
        child_nodes: true,
        outcomes: [IN_RANGE, OUT_OF_RANGE],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CHECK_PREVIOUS_RANGE]: {
        template: (values) => {
          const parts = [];
          if (values.subtest) parts.push(`Check subtest "${values.subtest}"`);
          if (values.drop_perc != null)
            parts.push(`drop ≥ ${values.drop_perc}%`);
          if (values.gain_perc != null)
            parts.push(`gain ≥ ${values.gain_perc}%`);
          if (values.previous_x_days_or_report)
            parts.push(`over ${values.previous_x_days_or_report}`);
          if (values.previous_x_value != null)
            parts.push(`${values.previous_x_value} times`);
          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Subtest:",
            var: "subtest",
            widget: "TextField",
            type: "string",
          },
          {
            label: "Drop %:",
            var: "drop_perc",
            widget: "TextField",
            type: "int",
          },
          {
            label: "Gain %:",
            var: "gain_perc",
            widget: "TextField",
            type: "int",
          },
          {
            label: "Search type:",
            var: "previous_x_days_or_report",
            widget: "RadioButton",
            type: "text",
            values: ["X Previous Days", "X Previous Reports"],
          },
          {
            label: "X Value:",
            var: "previous_x_value",
            widget: "TextField",
            type: "int",
          },
        ],
        validate: validators[CHECK_PREVIOUS_RANGE],
        child_nodes: true,
        outcomes: [IN_PER_RANGE, OUT_OF_PER_RANGE],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [LEAVE_FOR_GP]: {
        template: (values) =>
          values.info_var
            ? `Leave for GP. Info: ${values.info_var}`
            : LEAVE_FOR_GP,
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Info:", var: "info_var" },
        ],
        validate: validators[LEAVE_FOR_GP],
        child_nodes: false,
      },
      [FILE_REPORT]: {
        template: (values) => {
          if (values.filing_approach === "No Comments")
            return "File report without comments";
          if (values.filing_comment)
            return `File report with comment: "${values.filing_comment}"`;
          return "File report";
        },
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Filing Approach:",
            var: "filing_approach",
            widget: "RadioButton",
            type: "text",
            values: ["With Comment", "No Comments"],
          },
          { label: "Filing Comment:", var: "filing_comment" },
        ],
        validate: validators[FILE_REPORT],
        child_nodes: false,
      },

      [SEND_ACCURX_MESSAGE]: {
        template: (values) =>
          values.accurx_message
            ? `Send Accurx message: "${values.accurx_message}"`
            : SEND_ACCURX_MESSAGE,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Message:", var: "accurx_message" },
        ],
        validate: () => ({}),
        child_nodes: false,
      },

      [KEYWORD_CHECK]: {
        template: (values) => {
          const parts = [KEYWORD_CHECK];
          if (values.keywords) parts.push(`for keywords: "${values.keywords}"`);
          if (values.check_type) parts.push(`must be ${values.check_type}`);
          if (values.source) parts.push(`in ${values.source}`);
          return parts.join(", ");
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Keywords to Check:", var: "keywords", widget: "ListItem" },
          {
            label: "Check type:",
            var: "check_type",
            widget: "RadioButton",
            type: "text",
            values: ["Present", "Absent"],
          },
          {
            label: "Source:",
            var: "source",
            widget: "RadioButton",
            type: "text",
            values: ["Report", "Problem Page"],
          },
        ],
        validate: () => ({}),
        child_nodes: true,
        outcomes: [FAILED, PASSED],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CHECK_MEDICATION_ISSUED]: {
        template: (values) => {
          const meds = values.medications
            ? `for medications: ${values.medications}`
            : "";
          const months = values.issued_in_months
            ? `within last ${values.issued_in_months} months`
            : "";
          return `Check Medication Issued ${meds} ${months}`.trim();
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Medication Issued in Last (Months):",
            var: "issued_in_months",
          },
          {
            label: "List of Medications:",
            var: "medications",
            widget: "ListItem",
          },
        ],
        validate: validators[CHECK_MEDICATION_ISSUED],
        child_nodes: true,
        outcomes: [MEDICATION_PRESENT, MEDICATION_NOT_PRESENT],
        children: [
          ALL_OF,
          ONE_OF,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
        ],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CALCULATE_QRISK]: {
        template: (values) =>
          values.protocol_name
            ? `Calculate QRisk using protocol ${values.protocol_name}`
            : CALCULATE_QRISK,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Protocol Name", var: "protocol_name" },
        ],
        validate: validators[CALCULATE_QRISK],
        child_nodes: true,
        children: [QRISK_RANGE_CHECK],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [QRISK_RANGE_CHECK]: {
        template: (values) => {
          if (values.min_val != null && values.max_val != null)
            return `Check QRisk value in range ${values.min_val} – ${values.max_val}`;
          if (values.min_val != null)
            return `Check QRisk value above ${values.min_val}`;
          if (values.max_val != null)
            return `Check QRisk value below ${values.max_val}`;
          return "Check QRisk value";
        },
        has_deactivated: true,
        has_terminate: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          {
            label: "Terminate:",
            var: "terminate",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "QRisk Minimum Value:", var: "min_val" },
          { label: "QRisk Maximum Value:", var: "max_val" },
        ],
        validate: validators[QRISK_RANGE_CHECK],
        child_nodes: false,
        outcomes: [INSIDE_QRISK_SCORE, OUTSIDE_QRISK_SCORE],
        outcome_children: [
          LAUNCH_PROTOCOL,
          CHECK_RANGE,
          CHECK_PREVIOUS_RANGE,
          KEYWORD_CHECK,
          CHECK_MEDICATION_ISSUED,
          CALCULATE_QRISK,
          ALL_OF,
          ONE_OF,
          FILE_REPORT,
          SEND_ACCURX_MESSAGE,
          CHANGE_OWNER,
          ADD_CODE,
          CREATE_TASK,
          LEAVE_FOR_GP,
        ],
      },

      [CHANGE_OWNER]: {
        template: (values) =>
          values.new_owner
            ? `Change Owner to ${values.new_owner}`
            : CHANGE_OWNER,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Change Owner To:", var: "new_owner" },
        ],
        validate: validators[CHANGE_OWNER],
        child_nodes: false,
      },

      [ADD_CODE]: {
        template: (values) =>
          values.code ? `Add code "${values.code}" to Care Record` : ADD_CODE,
        has_deactivated: true,
        fields_config: [
          {
            label: "Deactivate:",
            var: "deactivated",
            widget: "IconBox",
            type: "boolean",
          },
          { label: "Code to Add:", var: "code" },
        ],
        validate: validators[ADD_CODE],
        child_nodes: false,
      },
    },
  },

  reducers: {
    addChildNode: (state, action) => {
      const { parentId, nodeType } = action.payload;
      const newNode = {
        id: Date.now().toString(),
        node: nodeType,
        nodes: [],
        outcomes: {},
        fields: {},
      };

      function insertNode(current) {
        if (current.id === parentId) {
          if (!current.nodes) current.nodes = [];
          current.nodes.push(newNode);
          return true;
        }
        if (current.nodes && current.nodes.some((child) => insertNode(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => insertNode(child)),
          );
        }
        return false;
      }

      insertNode(state.root);
    },

    addOutcomeBranch: (state, action) => {
      const { nodeId, outcomeKey } = action.payload;

      function addBranch(current) {
        if (current.id === nodeId) {
          if (!current.outcomes) current.outcomes = {};
          if (!current.outcomes[outcomeKey]) current.outcomes[outcomeKey] = [];
          return true;
        }
        if (current.nodes && current.nodes.some((child) => addBranch(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => addBranch(child)),
          );
        }
        return false;
      }

      addBranch(state.root);
    },

    addOutcomeNode: (state, action) => {
      const { parentId, outcomeKey, nodeType } = action.payload;

      const newNode = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        node: nodeType,
        nodes: [],
        outcomes: {},
        fields: {},
      };

      function insertOutcomeNode(current) {
        if (current.id === parentId) {
          if (!current.outcomes[outcomeKey]) current.outcomes[outcomeKey] = [];
          current.outcomes[outcomeKey].push(newNode);
          return true;
        }
        if (
          current.nodes &&
          current.nodes.some((child) => insertOutcomeNode(child))
        )
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => insertOutcomeNode(child)),
          );
        }
        return false;
      }

      insertOutcomeNode(state.root);
    },

    updateNodeField: (state, action) => {
      const { nodeId, field, value } = action.payload;

      function updateNode(current) {
        if (current.id === nodeId) {
          current.fields[field] = value;

          // run validation after update
          const cfg = state.nodeConfigs[current.node];
          if (cfg && typeof cfg.validate === "function") {
            const errors = cfg.validate(current.fields || {});
            state.errors[nodeId] = errors;
          } else {
            state.errors[nodeId] = {};
          }
          return true;
        }
        if (current.nodes && current.nodes.some((child) => updateNode(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => updateNode(child)),
          );
        }
        return false;
      }

      updateNode(state.root);
    },

    deleteNode: (state, action) => {
      const { nodeId } = action.payload;

      // Handle root deletion explicitly
      if (state.root?.id === nodeId) {
        state.root = {};
        state.errors = {};
        return;
      }

      function removeNode(current) {
        if (current.nodes) {
          const idx = current.nodes.findIndex((child) => child.id === nodeId);
          if (idx !== -1) {
            current.nodes.splice(idx, 1);
            delete state.errors[nodeId];
            return true;
          }
          current.nodes.forEach((child) => removeNode(child));
        }
        if (current.outcomes) {
          Object.values(current.outcomes).forEach((arr) => {
            const idx = arr.findIndex((child) => child.id === nodeId);
            if (idx !== -1) {
              arr.splice(idx, 1);
              delete state.errors[nodeId];
            } else {
              arr.forEach((child) => removeNode(child));
            }
          });
        }
      }

      removeNode(state.root);
    },

    deleteOutcomeBranch: (state, action) => {
      const { nodeId, outcomeKey } = action.payload;

      function removeBranch(current) {
        if (current.id === nodeId && current.outcomes) {
          const branchNodes = current.outcomes[outcomeKey] || [];
          branchNodes.forEach((node) => delete state.errors[node.id]);
          delete current.outcomes[outcomeKey];
          return true;
        }
        if (current.nodes && current.nodes.some((child) => removeBranch(child)))
          return true;
        if (current.outcomes) {
          return Object.values(current.outcomes).some((arr) =>
            arr.some((child) => removeBranch(child)),
          );
        }
        return false;
      }

      removeBranch(state.root);
    },
    setNodeErrors: (state, action) => {
      const { nodeId, errors } = action.payload;
      state.errors[nodeId] = errors;
    },
  },

  extraReducers: (builder) => {
    builder
      // Save
      .addCase(saveRuleset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveRuleset.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(saveRuleset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.status || "Failed to save ruleset";
      })
      // Load
      .addCase(loadRuleset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadRuleset.fulfilled, (state, action) => {
        state.loading = false;
        state.root = action.payload; // overwrite tree with loaded one
        state.errors = {};
      })
      .addCase(loadRuleset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load ruleset";
      });
  },
});

export const {
  addChildNode,
  addOutcomeBranch,
  addOutcomeNode,
  updateNodeField,
  deleteNode,
  deleteOutcomeBranch,
  setNodeErrors,
} = rulesetSlice.actions;

export default rulesetSlice.reducer;
